/**
 * Notify - Sonner-based notification system
 * @module components/framework/notify
 */
import { Bell, Check, TriangleAlert } from "lucide-react";
import React from "react";
import { toast as sonnerToast } from "sonner";
import { cn } from "../utils";

const Toast = ({ type, title, description }) => {
  const icon = {
    error: <TriangleAlert className="text-destructive" />,
    success: <Check className="text-success" />,
    warning: <TriangleAlert className="text-warning" />,
  }[type] ?? <Bell className="text-slate-400" />;

  return (
    <div
      className={cn(
        "border-edge bg-background text-md w-[400px] rounded-xl border border-l-4 p-4 shadow-lg",
        {
          "border-l-success": type === "success",
          "border-l-destructive": type === "error",
          "border-l-warning": type === "warning",
        },
      )}
    >
      <div className="grid grid-cols-[40px_1fr] gap-4">
        <div className="flex w-full justify-center">{icon}</div>
        <div className="flex flex-col justify-center gap-1">
          <span className={cn({ "font-semibold": !!description })}>
            {title}
          </span>
          <span>{description}</span>
        </div>
      </div>
    </div>
  );
};

const toast = (type, message, description) => {
  return sonnerToast.custom((id) => {
    return (
      <Toast id={id} type={type} title={message} description={description} />
    );
  });
};

/**
 * Display a notification using Sonner
 * @param {string} type - Notification type: 'info', 'error', 'success', 'warning'
 * @param {string} message - The message to display
 */
export const notify = (type = "info", message, description) => {
  if (!["info", "error", "success", "warning"].includes(type)) {
    throw new Error(`notify: Invalid notification type: ${type}`);
  }

  toast(type, message, description);
};
